import { AudioPlayer, LoadedRegion } from "../AudioPlayer";
import { RegionInfo } from "../Track";
import { TrackInfo } from "../types";
import {
  PlaybackBackend,
  PlaybackBackendLoadOptions,
  ReadyStateListener,
} from "./PlaybackBackend";

export class LegacyPlaybackBackend implements PlaybackBackend {
  readonly kind = "legacy" as const;

  private audioPlayer = new AudioPlayer();

  async load(options: PlaybackBackendLoadOptions): Promise<void> {
    await this.audioPlayer.load(options);
  }

  get ready(): Promise<void> {
    return this.audioPlayer.ready;
  }

  onReadyStateChange(listener: ReadyStateListener): () => void {
    return this.audioPlayer.onReadyStateChange(listener);
  }

  play(timestamp: number): Promise<void> {
    this.audioPlayer.play(timestamp);
    return Promise.resolve();
  }

  pause(): void {
    this.audioPlayer.pause();
  }

  seek(timestamp: number): Promise<void> {
    return this.audioPlayer.seek(timestamp);
  }

  setMasterGain(gain: number): void {
    this.audioPlayer.setMasterGain(gain);
  }

  setPlaybackRate(rate: number): void {
    this.audioPlayer.setPlaybackRate(rate);
  }

  setTranspose(semitones: number): void {
    this.audioPlayer.setTranspose(semitones);
  }

  setTrackGain(trackId: string, gain: number): void {
    this.audioPlayer.setTrackGain(trackId, gain);
  }

  addTracks(options: { tracks: TrackInfo[]; regions: LoadedRegion[] }): void {
    this.audioPlayer.addTracks(options);
  }

  addEmptyTrack(id: string): void {
    this.audioPlayer.addEmptyTrack(id);
  }

  removeTrack(id: string): void {
    this.audioPlayer.removeTrack(id);
  }

  addRegionToTrack(trackId: string, region: LoadedRegion): void {
    this.audioPlayer.addRegionToTrack(trackId, region);
  }

  removeRegionFromTrack(trackId: string, regionId: string): void {
    const track = this.audioPlayer.getTrack(trackId);
    track?.removeRegion(regionId);
  }

  getRegionsInRange(trackId: string, start: number, end: number): RegionInfo[] {
    const track = this.audioPlayer.getTrack(trackId);

    if (!track) {
      return [];
    }

    return track.getRegionsInRange(start, end);
  }

  getRegionBuffer(trackId: string, regionId: string): AudioBuffer | undefined {
    return this.audioPlayer.getTrack(trackId)?.getSource(regionId)?.audioBuffer;
  }

  destroy(): void {
    this.audioPlayer.destroy();
  }
}
